<script setup lang="ts">
import { useYandexMetrika } from "yandex-metrika-vue3";
import { Right, Refresh } from "@element-plus/icons-vue";
import sanitizeHtml from "sanitize-html";
import { useWindowSize } from "@vueuse/core";
import { fetchOrderCreate, fetchOrderPreview } from "@/api";
import { OfferType } from "@/shared/enums";
import type { OfferItem } from "@/shared/types";
import { ElMessageBox, ElNotification } from "element-plus";
import { getCryptoBalanceAddress } from "@/api";
import {
  formatNumberWithSpaces,
  fiatConvert,
  numberExponentToLarge,
  cryptoConvert,
} from "@/shared/utils";

const ym = useYandexMetrika();

const { t } = useI18n();
const UsePaymentOptionsStore = usePaymentOptionsStore();
const { myOptions } = storeToRefs(UsePaymentOptionsStore);
const { getOptionById, getMyOptionById } = UsePaymentOptionsStore;

const UseCurrencyStore = useCurrencyStore();
const { getSympolById } = UseCurrencyStore;

const router = useRouter();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "addOption", BPO?: number[]): void;
}>();
const props = defineProps<{
  orderData: OfferItem;
  preSelectedOption?: number;
}>();

const { width: windowWidth } = useWindowSize();
const orderForm = reactive({
  sendAmount: "",
  receiveAmount: "",
  orderPayment: "" as string | number,
});
const rules = computed(() => {
  const rulesComb: Record<string, any[]> = {
    sendAmount: [
      {
        required: true,
        message: t("c-orderCreate-sendAmount-required"),
        trigger: "change",
      },
    ],
    receiveAmount: [
      {
        required: true,
        message: t("c-orderCreate-receiveAmount-required"),
        trigger: "change",
      },
    ],
    orderPayment: [
      {
        required: true,
        message: t("c-orderCreate-orderPayment-required"),
        trigger: "change",
      },
    ],
  };
  const fiatLimitsRules =
    props.orderData.type === OfferType.Sell
      ? rulesComb.sendAmount
      : rulesComb.receiveAmount;
  fiatLimitsRules.push({
    // @ts-ignore-next-line
    validator: (rule: any, value: string, callback: (error?: Error) => {}) => {
      const minLimit = +fiatConvert(props.orderData.minLimit, true);
      const maxLimit = +fiatConvert(props.orderData.maxLimit, true);
      if (+value < minLimit || +value > maxLimit) {
        callback(
          new Error(
            t("c-orderCreate-fiatLimits", {
              min: formatNumberWithSpaces(minLimit),
              max: formatNumberWithSpaces(maxLimit),
            })
          )
        );
      } else {
        callback();
      }
    },
    trigger: "change",
  });
  const cryptoLimitsRules =
    props.orderData.type === OfferType.Buy
      ? rulesComb.sendAmount
      : rulesComb.receiveAmount;
  cryptoLimitsRules.push({
    // @ts-ignore-next-line
    validator: (rule: any, value: string, callback: (error?: Error) => {}) => {
      const coinLimit = +cryptoConvert(
        props.orderData.offerAmount,
        props.orderData.type === OfferType.Buy
          ? props.orderData.currencyIn
          : props.orderData.currencyOut,
        true
      );
      if (+value > coinLimit) {
        callback(
          new Error(t("c-orderCreate-cryptoLimits", { limit: coinLimit }))
        );
      } else {
        callback();
      }
    },
    trigger: "change",
  });
  return rulesComb;
});
const orderFormRef = ref<null | any>(null);

const dialogVisible = ref(true);
const ratesChangedDialog = ref(false);
const newOfferData = ref();
const offerData = computed<OfferItem>(
  () => newOfferData.value || props.orderData
);
const offerPriceFetched = ref<number | null>(null);
const exchangeRate = computed(
  () => offerPriceFetched.value || offerData.value.exchangeRate
);
const fullscreen = computed(() => windowWidth.value < 820);

const handleClose = (done: () => void) => {
  ElMessageBox.confirm(t("c-orderCreate-closeOrderConfirm"), {
    confirmButtonText: t("c-orderCreate-close"),
    cancelButtonText: t("c-orderCreate-closeCancel"),
  })
    .then(() => {
      done();
    })
    .catch(() => {});
};
const sellAll = () => {
  const replaceAmount = currentBalance.value.replaceAll(" ", "");
  orderForm.sendAmount = replaceAmount;
  calcAmount(replaceAmount, undefined);
};
let abortController: AbortController | null = null;
const sendLoading = ref<boolean | ReturnType<typeof setTimeout>>(false);
const receiveLoading = ref<boolean | ReturnType<typeof setTimeout>>(false);
const fetchedAmount = reactive({
  amountCrypto: 0,
  amountFiat: 0,
});
const lastFetched: Record<string, string | undefined> = {
  send: undefined,
  receive: undefined,
};
const feeShown = ref<number>(0);
const calcAmount = async (send?: string, receive?: string, force?: boolean) => {
  if (!force) {
    lastFetched.send = send;
    lastFetched.receive = receive;
  } else {
    send = lastFetched.send;
    receive = lastFetched.receive;
  }
  abortController?.abort("APR");
  clearTimeout(sendLoading.value as ReturnType<typeof setTimeout>);
  clearTimeout(receiveLoading.value as ReturnType<typeof setTimeout>);
  sendLoading.value = false;
  receiveLoading.value = false;
  if (send) {
    if (send[0] === "0" && send[1] && send[1] !== ".") {
      orderForm.sendAmount = send[1];
    }
  } else if (receive) {
    if (receive[0] === "0" && receive[1] && receive[1] !== ".") {
      orderForm.receiveAmount = receive[1];
    }
  }
  if (send !== undefined && +send === 0) {
    orderForm.receiveAmount = "0";
    feeShown.value = 0;
    return;
  }
  if (receive !== undefined && +receive === 0) {
    orderForm.sendAmount = "0";
    feeShown.value = 0;
    return;
  }
  if (send !== undefined) {
    receiveLoading.value = setTimeout(() => {
      receiveLoading.value = true;
    }, 1000);
  } else if (receive !== undefined) {
    sendLoading.value = setTimeout(() => {
      sendLoading.value = true;
    }, 1000);
  }
  let currencyType: "crypto" | "fiat" = "crypto";
  if (
    (send !== undefined && props.orderData.type === OfferType.Sell) ||
    (receive !== undefined && props.orderData.type === OfferType.Buy)
  ) {
    currencyType = "fiat";
  }
  const rawAmount = send || receive || 0;

  const amount =
    currencyType === "crypto"
      ? cryptoConvert(+rawAmount, props.orderData.currencyIn)
      : +rawAmount * 100;

  let res;
  try {
    abortController = new AbortController();
    res = await fetchOrderPreview(
      props.orderData.offerId,
      currencyType,
      amount,
      abortController.signal
    );
    if (props.orderData.type === OfferType.Buy) {
      feeShown.value = res.fee;
    }
    fetchedAmount.amountCrypto = res.amountCrypto;
    fetchedAmount.amountFiat = res.amountFiat;
    const cryptoAmountReturned =
      res.amountCrypto === 0
        ? res.amountCrypto
        : numberExponentToLarge(
            cryptoConvert(res.amountCrypto, props.orderData.currencyIn, true)
          );

    if (send !== undefined) {
      orderForm.receiveAmount =
        props.orderData.type === OfferType.Sell
          ? cryptoAmountReturned.toString()
          : (res.amountFiat / 100).toString();
    } else if (receive !== undefined) {
      orderForm.sendAmount =
        props.orderData.type === OfferType.Sell
          ? (res.amountFiat / 100).toString()
          : cryptoAmountReturned.toString();
    }
    abortController = null;
  } catch (error: any) {}
  clearTimeout(sendLoading.value as ReturnType<typeof setTimeout>);
  clearTimeout(receiveLoading.value as ReturnType<typeof setTimeout>);
  sendLoading.value = false;
  receiveLoading.value = false;
  return res;
};
const loading = ref(false);
const localePath = useLocalePath();
const createOrder = async () => {
  orderFormRef.value?.validate?.(async (valid: boolean) => {
    if (!valid) {
      return;
    }
    loading.value = true;
    try {
      const res = await fetchOrderCreate({
        ...fetchedAmount,
        offerId: props.orderData.offerId,
        bpoId: Number(orderForm.orderPayment),
      });
      ElMessage.success(
        t("c-orderCreate-orderCreated", { id: res.createdOrderId })
      );
      setTimeout(() => {
        loading.value = false;
        emit("close");
        router.push(localePath(`/trade/orders/${res.createdOrderId}`));
      }, 900);
      ym.reachGoal("p2p_order_created");
    } catch (e: any) {
      loading.value = false;
      let title = t("c-orderCreate-errorTitle");
      let message =
        e?.data?.data?.message || t("c-orderCreate-errorCreatingOrder");
      switch (e?.data?.data.code) {
        case 1450:
          calcAmount(undefined, undefined, true);
          newOfferData.value = e?.data?.data.offer;
          ratesChangedDialog.value = true;
          return;
      }
      ElNotification({
        title,
        message,
        type: "error",
        duration: 5000,
      });
    }
  });
};
const paymentOptions = computed(() => {
  const intersection: any[] = [];
  myOptions.value.forEach((option: any) => {
    if (props.orderData.basePaymentOptions.includes(option.base_id)) {
      intersection.push({
        ...option,
        name: getOptionById(option.base_id)?.name,
      });
    }
  });
  return intersection;
});
const currentBalance = ref("");
const priceChanged = ref(false);
const updatePriceLoading = ref(false);
const updatePrice = async () => {
  updatePriceLoading.value = true;
  let res;
  if (lastFetched.send || lastFetched.receive) {
    res = await calcAmount(undefined, undefined, true);
  } else {
    res = await fetchOrderPreview(props.orderData.offerId, "fiat", 1);
  }
  if (res && res.exchangeRate !== exchangeRate.value) {
    priceChanged.value = true;
    offerPriceFetched.value = res.exchangeRate;
  } else {
    priceChanged.value = false;
  }
  updatePriceLoading.value = false;
};

onMounted(async () => {
  updatePrice();
  if (props.orderData.type === OfferType.Buy) {
    const balanceData = await getCryptoBalanceAddress(
      props.orderData.currencyIn
    );
    currentBalance.value = formatNumberWithSpaces(
      numberExponentToLarge(
        cryptoConvert(
          balanceData.balance - balanceData.frozenBalance,
          props.orderData.currencyIn,
          true
        ),
        10
      )
    );
  }
});
const onAddOption = () => {
  emit("addOption", props.orderData.basePaymentOptions);
};
watch(
  () => props.preSelectedOption,
  (val) => {
    const found = val && getMyOptionById(val)?.base_id;
    if (found) {
      orderForm.orderPayment = found;
    }
  }
);
</script>

<template>
  <div>
    <el-dialog
      v-model="ratesChangedDialog"
      width="600px"
      :fullscreen="fullscreen"
      destroy-on-close
      :title="t('c-orderCreate-ratesChangedTitle')"
      @close="loading = false"
    >
      <div class="mb-3">
        <el-alert type="warning" :closable="false">
          {{ t("c-orderCreate-ratesChangedAlert") }}
        </el-alert>
      </div>
      <p>
        {{ t("c-orderCreate-ratesChangedDesc") }}
      </p>
      <div class="mb-4">
        {{ t("c-orderCreate-oldPrice") }}:
        <b class="color-danger">
          {{
            formatNumberWithSpaces(
              fiatConvert(
                props.orderData.exchangeRate,
                true,
                props.orderData.exchangeRateUnit || 100
              )
            )
          }}

          {{ getSympolById(offerData.currencyOut) }}
        </b>
        <el-icon class="vertical-middle mr-2 ml-2"><Right /></el-icon>
        {{ t("c-orderCreate-newPrice") }}:
        <b class="color-success">
          {{
            formatNumberWithSpaces(
              fiatConvert(
                exchangeRate,
                true,
                props.orderData.exchangeRateUnit || 100
              )
            )
          }}

          {{ getSympolById(offerData.currencyOut) }}
        </b>
      </div>
      <div>
        1
        {{ getSympolById(offerData.currencyIn) }} =
        <b class="color-danger decoration-line-through mr-1">
          {{
            formatNumberWithSpaces(
              fiatConvert(
                props.orderData.exchangeRate,
                true,
                props.orderData.exchangeRateUnit || 100
              )
            )
          }}

          {{ getSympolById(offerData.currencyOut) }}
        </b>
        <b class="color-success">
          {{
            formatNumberWithSpaces(
              fiatConvert(
                exchangeRate,
                true,
                props.orderData.exchangeRateUnit || 100
              )
            )
          }}

          {{ getSympolById(offerData.currencyOut) }}
        </b>
      </div>
      <template #footer>
        <el-button @click="ratesChangedDialog = false" round>
          {{ t("c-orderCreate-returnToOrder") }}
        </el-button>
        <el-button
          round
          type="primary"
          @click="
            ratesChangedDialog = false;
            createOrder();
          "
          :loading="loading"
        >
          {{ t("c-orderCreate-proceedWithNewPrice") }}
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      v-model="dialogVisible"
      destroy-on-close
      :title="
        t('c-orderCreate-creatingOrderTitle', {
          type:
            props.orderData.type === OfferType.Sell
              ? t('c-orderCreate-buying')
              : t('c-orderCreate-selling'),
          currency: props.orderData.currencyIn,
        })
      "
      class=""
      width="800px"
      :fullscreen="fullscreen"
      :before-close="handleClose"
      @close="emit('close')"
    >
      <el-row :gutter="30">
        <el-col :span="12" :xs="24">
          <OffersTraderInfo :offer-creator="offerData.offerCreator" />
          <div class="mb-5 mt-5">
            <el-row>
              <el-col :span="-1" class="opacity-60">
                {{ t("c-orderCreate-paymentTimeLimit") }}
              </el-col>
              <div class="flex-grow"></div>
              <el-col :span="-1"> {{ t("c-orderCreate-30minutes") }} </el-col>
            </el-row>
            <el-row>
              <el-col :span="-1" class="opacity-60">
                {{ t("c-orderCreate-avgPaymentTime") }}
              </el-col>
              <div class="flex-grow"></div>
              <el-col :span="-1">
                {{ offerData.offerCreator?.averagePaymentTime }}
                {{ t("c-orderCreate-minutes") }}
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="-1" class="opacity-60">
                {{ t("c-orderCreate-available") }}
              </el-col>
              <div class="flex-grow"></div>
              <el-col :span="-1">
                {{
                  cryptoConvert(
                    offerData.offerAmount,
                    offerData.currencyIn,
                    true
                  )
                }}
                {{ offerData.currencyIn }}
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="-1" class="opacity-60">
                {{ t("c-orderCreate-paymentOptions") }}:
              </el-col>
              <div class="flex-grow"></div>
              <el-col :span="-1">
                <span
                  v-for="(option, i) in offerData.basePaymentOptions"
                  class="text-wrap"
                  ><span v-if="i !== 0">, </span>
                  {{ getOptionById(option)?.name }}
                </span>
              </el-col>
            </el-row>
          </div>
          <el-scrollbar max-height="300px">
            <div class="order-info">
              {{ t("c-orderCreate-traderInfo") }}:
              <div
                class="info-cont font-size-4"
                v-if="orderData.termsOfUse && orderData.termsOfUse !== ''"
                v-html="
                  sanitizeHtml(orderData.termsOfUse, {
                    allowedTags: ['h3', 'i', 'u', 'strong', 'p', 'br'],
                  })
                "
              ></div>
              <div v-else class="font-size-3 opacity-65">
                {{ t("c-orderCreate-noDescriptionSpecified") }}
              </div>
            </div>
          </el-scrollbar>
        </el-col>
        <el-col :span="12" :xs="24" class="relative">
          <div class="font-size-5">
            <span class="opacity-60">{{ t("c-orderCreate-priceLabel") }}:</span>
            {{
              formatNumberWithSpaces(
                fiatConvert(
                  exchangeRate,
                  true,
                  props.orderData.exchangeRateUnit || 100
                )
              )
            }}
            {{ getSympolById(offerData.currencyOut) }}
            <el-button
              circle
              size="small"
              class="ml-1"
              text
              :icon="Refresh"
              :loading="updatePriceLoading"
              @click="updatePrice"
            >
            </el-button>
            <el-tooltip
              class="box-item"
              effect="dark"
              :content="t('c-orderCreate-priceChangedTooltip')"
              placement="top-start"
            >
              <Transition>
                <span
                  class="font-size-3 opacity-55 cursor-pointer"
                  v-if="priceChanged"
                >
                  {{ t("c-orderCreate-priceChanged") }}
                </span>
              </Transition>
            </el-tooltip>
          </div>
          <p class="mb-3">
            <span class="opacity-55"
              >{{ t("c-orderCreate-platformFee") }}:</span
            >
            {{
              offerData.type === OfferType.Buy ? offerData.platformFeePer : 0
            }}% ({{ t("c-orderCreate-paidBySeller") }})
          </p>
          <el-form ref="orderFormRef" :model="orderForm" :rules="rules">
            <el-card class="mb-3" v-loading="sendLoading === true">
              <div>
                {{
                  t("c-orderCreate-iWantTo", {
                    type:
                      offerData.type === OfferType.Sell
                        ? t("c-orderCreate-pay")
                        : t("c-orderCreate-sell"),
                  })
                }}
              </div>
              <el-form-item prop="sendAmount">
                <el-input
                  :disabled="loading"
                  type="number"
                  :controls="false"
                  v-model="orderForm.sendAmount"
                  @input="calcAmount(orderForm.sendAmount)"
                  :placeholder="t('c-orderCreate-enterAmount')"
                >
                  <template #append>
                    {{
                      offerData.type === OfferType.Sell
                        ? offerData.currencyOut
                        : offerData.currencyIn
                    }}
                  </template>
                </el-input>
              </el-form-item>
              <div class="mt-1" v-if="offerData.type === OfferType.Sell">
                {{ t("c-orderCreate-limits") }}:
                {{
                  formatNumberWithSpaces(fiatConvert(offerData.minLimit, true))
                }}
                -
                {{
                  formatNumberWithSpaces(fiatConvert(offerData.maxLimit, true))
                }}
                {{ getSympolById(offerData.currencyOut) }}
              </div>
              <Transition>
                <div v-if="feeShown" class="flex items-baseline opacity-55">
                  {{ t("c-orderCreate-fee") }}:
                  {{
                    formatNumberWithSpaces(
                      numberExponentToLarge(
                        cryptoConvert(feeShown, offerData.currencyIn, true)
                      )
                    )
                  }}
                  {{ offerData.currencyIn }}
                </div>
              </Transition>
              <Transition>
                <div
                  class="mt-1 flex items-baseline"
                  v-if="offerData.type === OfferType.Buy && currentBalance"
                >
                  {{ t("c-orderCreate-myBalance") }}: {{ currentBalance }}
                  {{ offerData.currencyIn }}
                  <el-button
                    type="primary"
                    size="small"
                    text
                    @click="sellAll"
                    round
                  >
                    {{ t("c-orderCreate-sellAll") }}
                  </el-button>
                </div>
              </Transition>
            </el-card>
            <el-card class="mb-10" v-loading="receiveLoading === true">
              <div>{{ t("c-orderCreate-iWillReceive") }}</div>
              <el-form-item prop="receiveAmount">
                <el-input
                  :disabled="loading"
                  type="number"
                  :controls="false"
                  v-model="orderForm.receiveAmount"
                  :placeholder="t('c-orderCreate-enterAmount')"
                  @input="calcAmount(undefined, orderForm.receiveAmount)"
                >
                  <template #append>
                    {{
                      offerData.type === OfferType.Buy
                        ? offerData.currencyOut
                        : offerData.currencyIn
                    }}
                  </template>
                </el-input>
              </el-form-item>
              <div class="mt-1" v-if="offerData.type === OfferType.Buy">
                {{ t("c-orderCreate-limits") }}:
                {{
                  formatNumberWithSpaces(fiatConvert(offerData.minLimit, true))
                }}
                -
                {{
                  formatNumberWithSpaces(fiatConvert(offerData.maxLimit, true))
                }}
                {{ getSympolById(offerData.currencyOut) }}
              </div>
            </el-card>
            <el-card>
              {{ t("c-orderCreate-paymentMethod") }}:
              <el-form-item prop="orderPayment">
                <el-select
                  v-model="orderForm.orderPayment"
                  :disabled="loading"
                  placeholder="-"
                >
                  <template #empty>
                    {{ t("c-orderCreate-noPaymentOptions") }}
                  </template>
                  <el-option
                    v-for="item in paymentOptions"
                    :key="item.base_id"
                    :label="item.name + ' - ' + item.currency"
                    :value="item.base_id"
                  />
                  <template
                    #footer
                    v-if="
                      paymentOptions.length <
                      orderData.userPaymentOptions.length
                    "
                  >
                    <p>
                      <span :span="-1" class="opacity-60">
                        {{ t("c-orderCreate-alsoAvailable") }}
                      </span>
                      <span
                        v-for="(
                          option, i
                        ) in offerData.basePaymentOptions.filter(
                          (item) =>
                            !paymentOptions
                              .map((el) => el.base_id)
                              .includes(item)
                        )"
                        class="text-wrap"
                        ><span v-if="i !== 0">, </span>
                        {{ getOptionById(option)?.name }}
                      </span>
                    </p>
                    <el-button
                      size="small"
                      class="mr-1"
                      @click="onAddOption"
                      round
                    >
                      {{ t("c-orderCreate-addPaymentOption") }}
                    </el-button>
                  </template>
                </el-select>
              </el-form-item>
            </el-card>
          </el-form>
        </el-col>
      </el-row>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="emit('close')" round>{{
            t("c-orderCreate-cancel")
          }}</el-button>
          <el-button
            type="primary"
            @click="createOrder"
            :loading="loading"
            round
          >
            {{
              offerData.type === OfferType.Buy
                ? t("c-orderCreate-sell")
                : t("c-orderCreate-buy")
            }}
            {{ offerData.currencyIn.toUpperCase() }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.info-cont {
  border-radius: 3px;
  background-color: #8883;
  padding: 10px;
  padding-left: 20px;
  border-left: 2px solid;
  overflow-y: auto;
}
</style>
